import React, { useEffect, useState } from 'react';
import { authAxios, API } from '../utils/authAxios';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Loader2, Send, Eye, RefreshCw, Mail, AlertTriangle, CheckCircle2, BarChart3, Copy, XCircle } from 'lucide-react';

const AUDIENCE_OPTIONS = [
  { value: '', label: 'Todos (centros + profesionales + talleres)' },
  { value: 'centro', label: 'Solo centros' },
  { value: 'profesional', label: 'Solo profesionales' },
  { value: 'taller', label: 'Solo talleres' },
];

const PREVIEW_TYPES = [
  { value: 'centro', label: 'Centro' },
  { value: 'profesional', label: 'Profesional' },
  { value: 'taller', label: 'Taller' },
];

export default function AdminReto21Broadcast() {
  const [audienceInfo, setAudienceInfo] = useState(null);
  const [loadingAudience, setLoadingAudience] = useState(false);

  const [selectedAudience, setSelectedAudience] = useState('');
  const [maxSend, setMaxSend] = useState('');

  const [dryResult, setDryResult] = useState(null);
  const [dryRunning, setDryRunning] = useState(false);

  const [sendResult, setSendResult] = useState(null);
  const [sending, setSending] = useState(false);
  const [activeJob, setActiveJob] = useState(null);
  const [confirmText, setConfirmText] = useState('');

  const [previewType, setPreviewType] = useState('centro');
  const [previewSubject, setPreviewSubject] = useState('');
  const [previewHtml, setPreviewHtml] = useState('');
  const [previewLoading, setPreviewLoading] = useState(false);

  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(false);

  const [bounces, setBounces] = useState(null);
  const [bouncesLoading, setBouncesLoading] = useState(false);

  // Cargar conteo de audiencia
  const fetchAudience = async () => {
    setLoadingAudience(true);
    try {
      const { data } = await authAxios.get(`${API}/admin/reto21-broadcast/audience`);
      setAudienceInfo(data);
      setPreviewSubject(data?.subject || '');
    } catch (e) {
      setAudienceInfo({ error: e?.response?.data?.detail || 'Error' });
    } finally {
      setLoadingAudience(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoadingAudience(true);
      try {
        const { data } = await authAxios.get(`${API}/admin/reto21-broadcast/audience`);
        if (!cancelled) {
          setAudienceInfo(data);
          setPreviewSubject(data?.subject || '');
        }
      } catch (e) {
        if (!cancelled) setAudienceInfo({ error: e?.response?.data?.detail || 'Error' });
      } finally {
        if (!cancelled) setLoadingAudience(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, []);

  // Preview HTML — carga inline en iframe (más fiable que window.open)
  const loadPreview = async (audience) => {
    setPreviewLoading(true);
    try {
      const { data } = await authAxios.get(`${API}/admin/reto21-broadcast/preview?audience=${audience}`);
      setPreviewHtml(data.html || '');
    } catch (e) {
      setPreviewHtml(`<p style="color:red; padding:20px; font-family:sans-serif;">Error: ${e?.response?.data?.detail || e.message}</p>`);
    } finally {
      setPreviewLoading(false);
    }
  };

  // Stats
  const fetchStats = async () => {
    setStatsLoading(true);
    try {
      const { data } = await authAxios.get(`${API}/admin/reto21-broadcast/stats`);
      setStats(data);
    } catch (e) {
      setStats({ error: e?.response?.data?.detail || 'Error' });
    } finally {
      setStatsLoading(false);
    }
  };

  // Bounces
  const fetchBounces = async () => {
    setBouncesLoading(true);
    try {
      const { data } = await authAxios.get(`${API}/admin/reto21-broadcast/bounces`);
      setBounces(data);
    } catch (e) {
      setBounces({ error: e?.response?.data?.detail || 'Error' });
    } finally {
      setBouncesLoading(false);
    }
  };

  const copyBouncedEmails = () => {
    if (!bounces?.bounces?.length) return;
    const txt = bounces.bounces.map((b) => b.email).join('\n');
    navigator.clipboard.writeText(txt).then(() => {
      alert(`${bounces.bounces.length} emails copiados al portapapeles.`);
    });
  };

  const runDryRun = async () => {
    setDryRunning(true);
    setDryResult(null);
    setSendResult(null);
    try {
      const params = new URLSearchParams({ dry_run: 'true' });
      if (selectedAudience) params.set('only_audience', selectedAudience);
      if (maxSend && Number(maxSend) > 0) params.set('max_send', String(Number(maxSend)));
      const { data } = await authAxios.post(`${API}/admin/reto21-broadcast/send?${params}`);
      setDryResult(data);
    } catch (e) {
      setDryResult({ error: e?.response?.data?.detail || 'Error' });
    } finally {
      setDryRunning(false);
    }
  };

  // Polling del job activo
  useEffect(() => {
    if (!activeJob?.job_id || activeJob.status !== 'running') return;
    let cancelled = false;
    const poll = async () => {
      while (!cancelled) {
        await new Promise((r) => setTimeout(r, 2500));
        if (cancelled) return;
        try {
          const { data } = await authAxios.get(`${API}/admin/reto21-broadcast/job/${activeJob.job_id}`);
          if (cancelled) return;
          setActiveJob(data);
          if (data.status !== 'running') break;
        } catch {
          break;
        }
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [activeJob?.job_id, activeJob?.status]);

  // Detectar job en curso al montar
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await authAxios.get(`${API}/admin/reto21-broadcast/jobs/active`);
        if (!cancelled && Array.isArray(data.jobs) && data.jobs.length > 0) {
          setActiveJob(data.jobs[0]);
        }
      } catch { /* noop */ }
    })();
    return () => { cancelled = true; };
  }, []);

  const runRealSend = async () => {
    if (confirmText !== 'ENVIAR') {
      alert('Escribe ENVIAR para confirmar');
      return;
    }
    setSending(true);
    setSendResult(null);
    try {
      const params = new URLSearchParams({ dry_run: 'false' });
      if (selectedAudience) params.set('only_audience', selectedAudience);
      if (maxSend && Number(maxSend) > 0) params.set('max_send', String(Number(maxSend)));
      const { data } = await authAxios.post(`${API}/admin/reto21-broadcast/send?${params}`);
      setSendResult(data);
      if (data.job_id) {
        // Inicializar tracking del job
        setActiveJob({
          job_id: data.job_id,
          status: 'running',
          total: data.total || 0,
          sent: 0,
          skipped: 0,
          errors_count: 0,
        });
      }
      setConfirmText('');
    } catch (e) {
      setSendResult({ error: e?.response?.data?.detail || 'Error' });
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-reto21-broadcast">
      <div>
        <h2 className="text-2xl font-bold text-slate-50 mb-1">📊 Broadcast Estudio Reto 21</h2>
        <p className="text-sm text-slate-400">Envía el email B2B con los datos del estudio a centros, profesionales y talleres registrados.</p>
      </div>

      {/* 1 — Audiencia */}
      <Card className="bg-slate-900 border-slate-800">
        <CardHeader className="pb-3">
          <CardTitle className="text-slate-50 text-base flex items-center gap-2">
            <Mail className="w-4 h-4 text-emerald-400" /> 1 · Audiencia disponible
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-0">
          {loadingAudience ? (
            <div className="flex items-center gap-2 text-slate-400 text-sm"><Loader2 className="w-4 h-4 animate-spin" /> Cargando…</div>
          ) : audienceInfo?.error ? (
            <p className="text-red-400 text-sm">{audienceInfo.error}</p>
          ) : audienceInfo ? (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Metric label="Centros" value={audienceInfo.totals?.centro || 0} />
              <Metric label="Profesionales" value={audienceInfo.totals?.profesional || 0} />
              <Metric label="Talleres" value={audienceInfo.totals?.taller || 0} />
              <Metric label="TOTAL" value={audienceInfo.total || 0} accent />
            </div>
          ) : null}
          <div className="mt-3 flex items-center gap-2">
            <Button onClick={fetchAudience} disabled={loadingAudience} variant="outline" size="sm" data-testid="reto21-refresh-audience">
              <RefreshCw className="w-3 h-3 mr-1" /> Refrescar
            </Button>
            <span className="text-xs text-slate-500">Asunto: <span className="text-slate-300">{previewSubject || '—'}</span></span>
          </div>
        </CardContent>
      </Card>

      {/* 2 — Preview HTML inline */}
      <Card className="bg-slate-900 border-slate-800">
        <CardHeader className="pb-3">
          <CardTitle className="text-slate-50 text-base flex items-center gap-2">
            <Eye className="w-4 h-4 text-emerald-400" /> 2 · Preview del email
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-0">
          <div className="flex items-end gap-3 flex-wrap mb-4">
            <div>
              <label className="text-xs text-slate-400 mb-1 block">Variante</label>
              <select
                value={previewType}
                onChange={(e) => { setPreviewType(e.target.value); setPreviewHtml(''); }}
                className="px-3 py-2 bg-slate-950 border border-slate-700 rounded-lg text-slate-200 text-sm"
                data-testid="reto21-preview-type"
              >
                {PREVIEW_TYPES.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
              </select>
            </div>
            <Button onClick={() => loadPreview(previewType)} disabled={previewLoading} className="bg-emerald-600 hover:bg-emerald-700" data-testid="reto21-load-preview">
              {previewLoading ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Eye className="w-4 h-4 mr-1.5" />}
              {previewHtml ? 'Recargar' : 'Cargar preview'}
            </Button>
            {previewHtml && (
              <Button
                variant="outline"
                onClick={() => {
                  const w = window.open('about:blank', '_blank');
                  if (w) { w.document.write(previewHtml); w.document.close(); }
                }}
                data-testid="reto21-open-tab"
              >
                Abrir en nueva pestaña
              </Button>
            )}
          </div>
          {previewHtml ? (
            <iframe
              title="Preview email"
              srcDoc={previewHtml}
              className="w-full bg-white rounded-lg border border-slate-700"
              style={{ height: '720px' }}
              data-testid="reto21-preview-iframe"
              sandbox="allow-same-origin"
            />
          ) : (
            <div className="bg-slate-950 rounded-lg border border-dashed border-slate-700 p-10 text-center text-slate-500 text-sm">
              Selecciona una variante y click &quot;Cargar preview&quot; para verlo aquí mismo.
            </div>
          )}
        </CardContent>
      </Card>

      {/* 3 — Filtros y dry-run */}
      <Card className="bg-slate-900 border-slate-800">
        <CardHeader className="pb-3">
          <CardTitle className="text-slate-50 text-base flex items-center gap-2">
            <CheckCircle2 className="w-4 h-4 text-emerald-400" /> 3 · Configurar y simular envío
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-0 space-y-3">
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-slate-400 mb-1 block">Audiencia</label>
              <select
                value={selectedAudience}
                onChange={(e) => setSelectedAudience(e.target.value)}
                className="w-full px-3 py-2 bg-slate-950 border border-slate-700 rounded-lg text-slate-200 text-sm"
                data-testid="reto21-audience-select"
              >
                {AUDIENCE_OPTIONS.map((o) => <option key={o.value || 'all'} value={o.value}>{o.label}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs text-slate-400 mb-1 block">Límite (0 o vacío = sin límite)</label>
              <input
                type="number"
                min="0"
                value={maxSend}
                onChange={(e) => setMaxSend(e.target.value)}
                placeholder="Ej: 5 para test"
                className="w-full px-3 py-2 bg-slate-950 border border-slate-700 rounded-lg text-slate-200 text-sm"
                data-testid="reto21-max-send"
              />
            </div>
          </div>
          <Button onClick={runDryRun} disabled={dryRunning} className="bg-amber-600 hover:bg-amber-700" data-testid="reto21-dry-run">
            {dryRunning ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Eye className="w-4 h-4 mr-2" />}
            Simular (dry-run, no envía)
          </Button>

          {dryResult && (
            <div className="mt-2 p-3 rounded-lg bg-slate-950 border border-slate-700 text-sm" data-testid="reto21-dry-result">
              {dryResult.error ? (
                <p className="text-red-400">{dryResult.error}</p>
              ) : (
                <>
                  <p className="text-slate-200 mb-1.5"><strong>Destinatarios a enviar:</strong> <span className="text-emerald-300">{dryResult.to_send_count}</span></p>
                  <p className="text-xs text-slate-400 mb-2">
                    Centros: {dryResult.by_audience?.centro || 0} · Profesionales: {dryResult.by_audience?.profesional || 0} · Talleres: {dryResult.by_audience?.taller || 0}
                  </p>
                  {Array.isArray(dryResult.sample) && dryResult.sample.length > 0 && (
                    <>
                      <p className="text-xs text-slate-500 mb-1">Muestra (primeros 5):</p>
                      <ul className="text-xs text-slate-400 space-y-0.5">
                        {dryResult.sample.map((s, i) => (
                          <li key={i}>• <span className="text-emerald-400">{s.email}</span> ({s.audience}) {s.name && `— ${s.name}`}</li>
                        ))}
                      </ul>
                    </>
                  )}
                </>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* 4 — Envío real */}
      <Card className="bg-slate-900 border-red-500/30">
        <CardHeader className="pb-3">
          <CardTitle className="text-slate-50 text-base flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-red-400" /> 4 · Envío real (irreversible)
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-0 space-y-3">
          <p className="text-xs text-slate-400">
            Para confirmar, escribe <strong className="text-red-300">ENVIAR</strong> y presiona el botón. El sistema respeta la lista de unsubscribes automáticamente y mantiene rate-limit de ~1.2/s.
          </p>
          <div className="flex gap-2 items-end flex-wrap">
            <input
              type="text"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              placeholder='Escribe "ENVIAR"'
              className="px-3 py-2 bg-slate-950 border border-red-500/30 rounded-lg text-slate-200 text-sm w-40"
              data-testid="reto21-confirm-text"
              disabled={activeJob?.status === 'running'}
            />
            <Button
              onClick={runRealSend}
              disabled={sending || confirmText !== 'ENVIAR' || activeJob?.status === 'running'}
              className="bg-red-600 hover:bg-red-700 disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid="reto21-send-real"
            >
              {sending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
              {activeJob?.status === 'running' ? 'Envío en curso…' : 'Enviar broadcast ahora'}
            </Button>
          </div>

          {/* Progreso del job en curso */}
          {activeJob && (
            <div className="mt-3 p-3 rounded-lg bg-slate-950 border border-emerald-500/30 text-sm" data-testid="reto21-job-progress">
              <div className="flex items-center justify-between mb-2">
                <span className="text-slate-200 font-semibold">
                  {activeJob.status === 'running' && <><Loader2 className="w-4 h-4 inline mr-1.5 animate-spin text-emerald-400" /> Enviando…</>}
                  {activeJob.status === 'completed' && <><CheckCircle2 className="w-4 h-4 inline mr-1.5 text-emerald-400" /> Envío completado</>}
                  {activeJob.status === 'failed' && <><AlertTriangle className="w-4 h-4 inline mr-1.5 text-red-400" /> Falló</>}
                </span>
                <span className="text-[11px] text-slate-500">job {activeJob.job_id}</span>
              </div>
              {/* Barra de progreso */}
              <div className="w-full bg-slate-800 rounded-full h-2 overflow-hidden mb-2">
                <div
                  className="bg-emerald-500 h-2 rounded-full transition-all"
                  style={{ width: `${activeJob.total ? Math.min(100, Math.round(100 * ((activeJob.sent || 0) + (activeJob.skipped || 0) + (activeJob.errors_count || 0)) / activeJob.total)) : 0}%` }}
                />
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
                <span className="text-slate-300">Total: <strong>{activeJob.total || 0}</strong></span>
                <span className="text-emerald-300">Enviados: <strong>{activeJob.sent || 0}</strong></span>
                <span className="text-amber-300">Saltados: <strong>{activeJob.skipped || 0}</strong></span>
                <span className="text-red-300">Errores: <strong>{activeJob.errors_count || 0}</strong></span>
              </div>
              {activeJob.status === 'running' && (
                <p className="text-[11px] text-slate-500 mt-2">
                  El envío continúa en background. Puedes cerrar esta pestaña — los emails seguirán enviándose. ~1.2s por destinatario.
                </p>
              )}
              {activeJob.status !== 'running' && (
                <button
                  type="button"
                  onClick={() => { setActiveJob(null); fetchStats(); }}
                  className="mt-2 text-xs text-emerald-400 hover:text-emerald-300 underline"
                  data-testid="reto21-job-clear"
                >
                  Ocultar progreso y cargar estadísticas
                </button>
              )}
            </div>
          )}

          {sendResult && !activeJob && (
            <div className="mt-2 p-3 rounded-lg bg-slate-950 border border-slate-700 text-sm" data-testid="reto21-send-result">
              {sendResult.error ? (
                <p className="text-red-400">{sendResult.error}</p>
              ) : sendResult.queued === false ? (
                <p className="text-amber-300">{sendResult.message || 'Ya hay un envío en curso.'}</p>
              ) : (
                <p className="text-emerald-300">Envío encolado. Job <strong>{sendResult.job_id}</strong>.</p>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* 5 — Estadísticas */}
      <Card className="bg-slate-900 border-slate-800" data-testid="reto21-stats-card">
        <CardHeader className="pb-3 flex flex-row items-center justify-between">
          <CardTitle className="text-slate-50 text-base flex items-center gap-2">
            <BarChart3 className="w-4 h-4 text-emerald-400" /> 5 · Estadísticas del broadcast
          </CardTitle>
          <Button onClick={fetchStats} disabled={statsLoading} variant="outline" size="sm" data-testid="reto21-load-stats">
            {statsLoading ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <RefreshCw className="w-3 h-3 mr-1" />}
            {stats ? 'Refrescar' : 'Cargar estadísticas'}
          </Button>
        </CardHeader>
        <CardContent className="pt-0 space-y-4">
          {!stats && !statsLoading && (
            <p className="text-sm text-slate-500">Click &quot;Cargar estadísticas&quot; para ver los datos.</p>
          )}
          {stats && stats.error && (
            <p className="text-red-400 text-sm">{stats.error}</p>
          )}
          {stats && !stats.error && (
            <>
              {!stats.webhook_configured && (
                <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/30 text-xs text-amber-200">
                  <strong>⚠️ Tracking de open/click no detectado.</strong> Para activar las métricas de Resend, configura un webhook en <code className="bg-slate-950 px-1 rounded">resend.com/webhooks</code> apuntando a <code className="bg-slate-950 px-1 rounded">{API}/webhooks/resend</code> con los eventos <code className="bg-slate-950 px-1 rounded">email.delivered</code>, <code className="bg-slate-950 px-1 rounded">email.opened</code>, <code className="bg-slate-950 px-1 rounded">email.clicked</code>, <code className="bg-slate-950 px-1 rounded">email.bounced</code>.
                </div>
              )}

              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
                <Metric label="Enviados" value={stats.sent} accent />
                <Metric label="Entregados" value={stats.delivered} />
                <Metric label="Aperturas" value={stats.opened} suffix={stats.open_rate ? `${stats.open_rate}%` : null} />
                <Metric label="Clicks" value={stats.clicked} suffix={stats.click_rate ? `${stats.click_rate}%` : null} />
                <Metric label="Bounces" value={stats.bounced} suffix={stats.bounce_rate ? `${stats.bounce_rate}%` : null} />
                <Metric label="Open rate" value={`${stats.open_rate}%`} />
              </div>

              <div>
                <h4 className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-2">Por audiencia</h4>
                <div className="space-y-1.5">
                  {['centro', 'profesional', 'taller'].map((a) => (
                    <div key={a} className="flex items-center gap-3 text-xs text-slate-300 bg-slate-950 rounded px-3 py-2 border border-slate-800">
                      <span className="w-24 capitalize text-slate-400">{a}</span>
                      <span>Enviados: <strong className="text-slate-200">{stats.by_audience?.[a] || 0}</strong></span>
                      <span className="text-slate-600">·</span>
                      <span>Abiertos: <strong className="text-emerald-300">{stats.opens_by_audience?.[a] || 0}</strong></span>
                      <span className="text-slate-600">·</span>
                      <span>Clicks: <strong className="text-emerald-300">{stats.clicks_by_audience?.[a] || 0}</strong></span>
                    </div>
                  ))}
                </div>
              </div>

              {Array.isArray(stats.recent_sends) && stats.recent_sends.length > 0 && (
                <div>
                  <h4 className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-2">Últimos envíos</h4>
                  <div className="max-h-48 overflow-y-auto bg-slate-950 rounded border border-slate-800">
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="text-slate-500 border-b border-slate-800">
                          <th className="text-left p-2 font-medium">Email</th>
                          <th className="text-left p-2 font-medium">Audiencia</th>
                          <th className="text-left p-2 font-medium">Fecha</th>
                        </tr>
                      </thead>
                      <tbody>
                        {stats.recent_sends.map((s, i) => (
                          <tr key={i} className="border-b border-slate-900 text-slate-300">
                            <td className="p-2 truncate max-w-[260px]">{s.email}</td>
                            <td className="p-2 capitalize text-slate-400">{s.audience}</td>
                            <td className="p-2 text-slate-500 text-[11px]">{(s.sent_at || '').slice(0, 19).replace('T', ' ')}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* 6 — Bounces / quejas */}
      <Card className="bg-slate-900 border-red-500/30" data-testid="reto21-bounces-card">
        <CardHeader className="pb-3 flex flex-row items-center justify-between">
          <CardTitle className="text-slate-50 text-base flex items-center gap-2">
            <XCircle className="w-4 h-4 text-red-400" /> 6 · Bounces y quejas
          </CardTitle>
          <Button onClick={fetchBounces} disabled={bouncesLoading} variant="outline" size="sm" data-testid="reto21-load-bounces">
            {bouncesLoading ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <RefreshCw className="w-3 h-3 mr-1" />}
            {bounces ? 'Refrescar' : 'Cargar bounces'}
          </Button>
        </CardHeader>
        <CardContent className="pt-0 space-y-3">
          {!bounces && !bouncesLoading && (
            <p className="text-sm text-slate-500">Click &quot;Cargar bounces&quot; para ver los emails que rebotaron en este broadcast.</p>
          )}
          {bounces?.error && <p className="text-red-400 text-sm">{bounces.error}</p>}
          {bounces && !bounces.error && (
            <>
              <div className="flex items-center gap-3 flex-wrap text-sm">
                <span className="text-slate-200">Total: <strong className="text-red-300">{bounces.total}</strong></span>
                <span className="text-slate-600">·</span>
                <span className="text-slate-300">Bounces: <strong>{bounces.by_type?.bounced || 0}</strong></span>
                <span className="text-slate-300">Quejas: <strong>{bounces.by_type?.complained || 0}</strong></span>
                {bounces.total > 0 && (
                  <Button onClick={copyBouncedEmails} size="sm" variant="outline" className="ml-auto" data-testid="reto21-copy-bounces">
                    <Copy className="w-3 h-3 mr-1" /> Copiar emails
                  </Button>
                )}
              </div>

              {bounces.total === 0 ? (
                <p className="text-sm text-emerald-400 mt-2">✅ Sin bounces — todos los emails fueron aceptados por el servidor de destino.</p>
              ) : (
                <div className="max-h-72 overflow-y-auto bg-slate-950 rounded border border-slate-800">
                  <table className="w-full text-xs">
                    <thead className="bg-slate-900 sticky top-0">
                      <tr className="text-slate-500 border-b border-slate-800">
                        <th className="text-left p-2 font-medium">Email</th>
                        <th className="text-left p-2 font-medium">Fuente</th>
                        <th className="text-left p-2 font-medium">Nombre</th>
                        <th className="text-left p-2 font-medium">Tipo</th>
                        <th className="text-left p-2 font-medium">Razón</th>
                      </tr>
                    </thead>
                    <tbody>
                      {bounces.bounces.map((b, i) => (
                        <tr key={i} className="border-b border-slate-900 text-slate-300 hover:bg-slate-900/40">
                          <td className="p-2 truncate max-w-[220px] font-mono text-[11px]">{b.email}</td>
                          <td className="p-2 text-slate-400 capitalize text-[11px]">{b.source || '—'}</td>
                          <td className="p-2 text-slate-400 truncate max-w-[180px] text-[11px]">{b.name || '—'}</td>
                          <td className="p-2 text-[11px]">
                            <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${b.event_type === 'email.complained' ? 'bg-red-500/15 text-red-300' : 'bg-amber-500/15 text-amber-300'}`}>
                              {b.event_type === 'email.complained' ? 'queja' : (b.bounce_type || 'bounce')}
                            </span>
                          </td>
                          <td className="p-2 text-slate-400 text-[11px]">{b.reason || '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {bounces.total > 0 && (
                <p className="text-[11px] text-slate-500 leading-relaxed">
                  Los bounces ya están en la lista de supresión (no se reenviarán). Si reconoces algún email mal escrito, edítalo en la ficha del centro/profesional para futuras campañas.
                </p>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function Metric({ label, value, accent, suffix }) {
  return (
    <div className={`px-4 py-3 rounded-lg border ${accent ? 'bg-emerald-500/10 border-emerald-500/30' : 'bg-slate-950 border-slate-800'}`}>
      <div className={`text-2xl font-bold ${accent ? 'text-emerald-300' : 'text-slate-100'}`}>{value}</div>
      <div className="text-[10px] uppercase tracking-wider text-slate-500 mt-0.5">{label}{suffix ? <span className="text-emerald-400 font-semibold ml-1">· {suffix}</span> : null}</div>
    </div>
  );
}
